import { PlatformFeeSettings, PlatformSettingsService } from '../services/platformSettingsService';

/**
 * Calculate the total price including platform fees
 * @param basePrice - The base product price
 * @param feeSettings - Platform fee settings (optional, will be fetched if not provided)
 * @returns The total price including fees (rounded to 2 decimal places)
 */
export async function calculatePriceWithFees(
  basePrice: number,
  feeSettings?: PlatformFeeSettings
): Promise<number> {
  try {
    // Fetch fee settings if not provided
    const settings = feeSettings || await PlatformSettingsService.getPlatformFeeSettings();

    let totalPrice: number;
    if (settings.fee_type === 'fixed') {
      // Fixed fee: add the fixed amount to the base price
      totalPrice = basePrice + (settings.fee_amount || 0);
    } else {
      // Percentage fee: add the percentage of the base price
      const percentage = settings.fee_percentage || 0;
      totalPrice = basePrice + (basePrice * percentage / 100);
    }
    // Round down to 2 decimal places
    return Math.floor(totalPrice * 100) / 100;
  } catch (error) {
    console.error('Error calculating price with fees:', error);
    // Return base price rounded down to 2 decimals if there's an error
    return Math.floor(basePrice * 100) / 100;
  }
}

/**
 * Calculate the platform fee amount for a given base price
 * @param basePrice - The base product price
 * @param feeSettings - Platform fee settings (optional, will be fetched if not provided)
 * @returns The platform fee amount
 */
export async function calculatePlatformFee(
  basePrice: number,
  feeSettings?: PlatformFeeSettings
): Promise<number> {
  try {
    const settings = feeSettings || await PlatformSettingsService.getPlatformFeeSettings();

    if (settings.fee_type === 'fixed') {
      return settings.fee_amount || 0;
    } else {
      const percentage = settings.fee_percentage || 0;
      return basePrice * percentage / 100;
    }
  } catch (error) {
    console.error('Error calculating platform fee:', error);
    return 0;
  }
}

/**
 * Synchronous version using provided fee settings
 * @param basePrice - The base product price
 * @param feeSettings - Platform fee settings
 * @returns The total price including fees (rounded to 2 decimal places)
 */
export function calculatePriceWithFeesSync(
  basePrice: number,
  feeSettings: PlatformFeeSettings
): number {
  let totalPrice: number;
  if (feeSettings.fee_type === 'fixed') {
    totalPrice = basePrice + (feeSettings.fee_amount || 0);
  } else {
    const percentage = feeSettings.fee_percentage || 0;
    totalPrice = basePrice + (basePrice * percentage / 100);
  }
  // Round to 2 decimal places
  return Math.round(totalPrice * 100) / 100;
}

/**
 * Synchronous version to calculate platform fee
 * @param basePrice - The base product price
 * @param feeSettings - Platform fee settings
 * @returns The platform fee amount
 */
export function calculatePlatformFeeSync(
  basePrice: number,
  feeSettings: PlatformFeeSettings
): number {
  if (feeSettings.fee_type === 'fixed') {
    return feeSettings.fee_amount || 0;
  } else {
    const percentage = feeSettings.fee_percentage || 0;
    return basePrice * percentage / 100;
  }
}

/**
 * Calculate base price from target customer price (reverse calculation)
 * @param customerPrice - The target price customers will see (including fees)
 * @param feeSettings - Platform fee settings
 * @returns The base price needed to achieve the target customer price
 */
export function calculateBasePriceFromCustomerPrice(
  customerPrice: number,
  feeSettings: PlatformFeeSettings
): number {
  if (feeSettings.fee_type === 'fixed') {
    // For fixed: customer_price = base_price + fee
    // So: base_price = customer_price - fee
    const basePrice = customerPrice - (feeSettings.fee_amount || 0);
    return Math.max(0, Math.floor(basePrice * 100) / 100);
  } else {
    // For percentage: customer_price = base_price + (base_price * percentage / 100)
    // customer_price = base_price * (1 + percentage / 100)
    // base_price = customer_price / (1 + percentage / 100)
    const percentage = feeSettings.fee_percentage || 0;
    const basePrice = customerPrice / (1 + percentage / 100);
    return Math.max(0, Math.floor(basePrice * 100) / 100);
  }
}

/**
 * Calculate customer price from base price (forward calculation)
 * @param basePrice - The base product price
 * @param feeSettings - Platform fee settings
 * @returns The price customers will see (including fees)
 */
export function calculateCustomerPriceFromBasePrice(
  basePrice: number,
  feeSettings: PlatformFeeSettings
): number {
  return calculatePriceWithFeesSync(basePrice, feeSettings);
}

